// BISToyCertification.jsx - BIS TOY CERTIFICATION IMAGE
import React from "react";

export default function BISToyCertification() {
  return (
    <div className="min-h-screen bg-white">
      {/* HERO SECTION */}
      <section className="max-w-6xl mx-auto px-4 py-12 grid md:grid-cols-2 gap-10 items-center">
        {/* Left: Text */}
        <div>
          <p className="text-sm tracking-[0.25em] text-gray-500 uppercase mb-3">
            BIS Certification for Toys
          </p>
          <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
            BIS Certification for Toy Safety Standards
          </h1>
          <div className="w-20 h-1 bg-yellow-500 mb-6" />
          <p className="text-gray-700 mb-4">
            Hawkins &amp; Brooks assists manufacturers and importers with BIS
            Certification for Toys under Toys (Quality Control) Order 2020, mandatory
            for all toys for children under 14 years as per IS 9873 and IS 15644 standards.
          </p>
          <p className="text-gray-700">
            Through BIS Scheme-I (In-house quality control) or FMCS (Foreign Manufacturers
            Certification Scheme), the process ensures mechanical, chemical, flammability,
            and electrical safety compliance with ISI Mark authorization for legal sale.
          </p>
        </div>

        {/* Right: BIS TOY SAFETY CERTIFICATION IMAGE */}
        <div className="relative">
          <div className="absolute -inset-4 border border-yellow-400 rounded-3xl opacity-40 hidden md:block" />
          <div className="relative rounded-3xl overflow-hidden shadow-xl border border-gray-100 bg-gradient-to-br from-blue-50 to-indigo-50">
            <img
              src="https://images.unsplash.com/photo-1583842593980-2153c94f1a29?w=1200&h=1200&fit=crop&crop=entropy&auto=format&q=85"
              alt="BIS toy safety certification testing - IS 9873 standards lab with safety equipment and certification documents"
              className="w-full h-[400px] md:aspect-square object-cover block"
              loading="eager"
              onError={(e) => {
                e.target.src = "https://images.unsplash.com/photo-1574169208507-84376144848b?w=1200&h=1200&fit=crop&auto=format&q=85";
              }}
            />
          </div>
        </div>
      </section>

      {/* [Rest of the content remains exactly the same - unchanged] */}
      <section className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Professional Compliance Support by Hawkins &amp; Brooks
            </h2>
            <p className="text-gray-700 mb-4">
              Hawkins &amp; Brooks provides complete BIS toy certification support
              ensuring compliance across 14 IS 9873 parts and IS 15644 standards.
            </p>
            <div className="bg-gray-50 border border-yellow-100 rounded-xl p-5">
              <ul className="space-y-2 text-gray-800 list-disc list-inside">
                <li>Toy classification and standard selection (IS 9873 Parts 1-9 for mechanical/chemical safety, IS 15644 for electric toys).</li>
                <li>Application submission through Manak Online portal (Form VIII) with ₹1 Lakh fee and sample coordination to BIS notified labs.</li>
                <li>Factory inspection preparation for mechanical properties, chemical analysis, flammability testing, and age grading verification.</li>
                <li>License grant (valid 1-2 years) with ISI Mark authorization and ongoing market surveillance audit support.</li>
              </ul>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Documents Required for BIS Toy Certification
            </h2>
            <p className="text-gray-700 mb-4">
              Hawkins &amp; Brooks assists in preparing complete technical documentation
              required for BIS toy safety certification approval.
            </p>
            <div className="bg-gray-50 border border-yellow-100 rounded-xl p-5">
              <ul className="space-y-2 text-gray-800 list-disc list-inside">
                <li>Product specifications including toy type, age grading (0-3, 3-8, 8-14 years), materials composition, and design drawings.</li>
                <li>Test reports from BIS notified labs covering IS 9873 (migration of elements, phthalates, flammability, mechanical safety) and IS 15644 (electrical safety).</li>
                <li>Manufacturer details (factory layout, process flow, quality control plan) or importer information (FMCG application, AIR appointment letter).</li>
                <li>Labeling artwork, user warnings, and compliance declaration for 16 CFR 1500 and EN 71 harmonized with Indian toy safety standards.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Why Choose Hawkins &amp; Brooks?
            </h2>
            <p className="text-gray-700 mb-4">
              The firm specializes in toy safety compliance with proven success securing
              BIS certifications for domestic and foreign toy manufacturers.
            </p>
            <div className="space-y-3">
              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">Toy Safety Regulations Expertise</h3>
                <p className="text-gray-700 text-sm">Complete IS 9873 (14 parts) and IS 15644 knowledge with chemical, mechanical, flammability, and electrical safety compliance mastery.</p>
              </div>
              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">Accurate Compliance Documentation</h3>
                <p className="text-gray-700 text-sm">Professional test report structuring, factory inspection preparation, and Manak Online application ensuring first-time BIS license approval.</p>
              </div>
              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">End-to-End Certification Assistance</h3>
                <p className="text-gray-700 text-sm">Complete lifecycle support from toy classification through ISI Mark authorization, market surveillance, and annual renewal compliance.</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col justify-center">
            <div className="bg-gray-50 border border-yellow-100 rounded-2xl p-6 shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Legally Sell Certified Toys in India</h3>
              <p className="text-gray-700 mb-5">Connect with Hawkins &amp; Brooks to secure BIS Certification for your toys and legally manufacture, import, and distribute child-safe products across India's retail chains, e-commerce platforms, and toy stores nationwide.</p>
              <div className="flex flex-wrap gap-3">
                <a href="/contact" className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-green-500 text-black font-semibold shadow hover:bg-green-400 transition-colors text-sm">Get Consultation</a>
                <a href="tel:+919217468906" className="inline-flex items-center justify-center px-5 py-2.5 rounded-full border border-yellow-500 text-yellow-600 font-medium hover:bg-yellow-50 transition-colors text-sm">Call Hawkins &amp; Brooks</a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
